import { create } from 'zustand';
import httpClient from '../httpClient';
import { apiErrorMessage, splitRecipientName, combineRecipientName } from '../lib/utils';

const clientNotes = (value) => /\b(?:pt_api_|ip_api_|instaproof_|pt_discount\s*:)/i.test(value ?? '') ? '' : value ?? '';

const partitionIncluded = (included) => {
  const sorted = [...(included || [])].sort((a, b) =>
    String(a?.id ?? '').localeCompare(String(b?.id ?? ''), undefined, { numeric: true }),
  );
  return {
    lineItems: sorted.filter((i) => i?.type === 'api_lab_order_line_item'),
    multiOpeningLineItems: sorted.filter((i) => i?.type === 'multi_openings_line_item'),
    albumOrderDetails: sorted.filter((i) => i?.type === 'album_order_detail'),
  };
};

const mergeOrderData = (currentOrder, updatedOrder) => ({
  ...currentOrder,
  ...updatedOrder,
  attributes: {
    ...currentOrder?.attributes,
    ...updatedOrder?.attributes,
  },
});

const mergeOrderResponse = (currentOrderResponse, updatedOrderResponse) => ({
  ...currentOrderResponse,
  ...updatedOrderResponse,
  data: mergeOrderData(currentOrderResponse?.data, updatedOrderResponse?.data),
});

const useLabOrderDetailsStore = create((set, get) => ({
  // Order data
  order: null,
  setOrder: (order) => set({ order }),
  
  // Lab Order ID
  labOrderId: null,
  setLabOrderId: (labOrderId) => set({ labOrderId }),

  // Orders list and metadata
  orders: null,
  setOrders: (orders) => set({ orders }),
  metadata: null,
  setMetadata: (metadata) => set({ metadata }),

  // Admin flag
  isAdmin: false,
  setIsAdmin: (isAdmin) => set({ isAdmin }),

  // Line items state (print/canvas only — not album_order_detail)
  lineItems: [],
  originalLineItems: [],
  filteredLineItems: [],
  albumOrderDetails: [],
  filteredAlbumOrderDetails: [],
  multiOpeningLineItems: [],
  filteredMultiOpeningLineItems: [],
  setLineItems: (included) => {
    const { lineItems, multiOpeningLineItems, albumOrderDetails } = partitionIncluded(included);
    set({
      lineItems,
      originalLineItems: lineItems,
      filteredLineItems: lineItems,
      albumOrderDetails,
      filteredAlbumOrderDetails: albumOrderDetails,
      multiOpeningLineItems,
      filteredMultiOpeningLineItems: multiOpeningLineItems,
    });
  },
  setFilteredLineItems: (filteredLineItems) => set({ filteredLineItems }),
  setFilteredAlbumOrderDetails: (filteredAlbumOrderDetails) =>
    set({ filteredAlbumOrderDetails }),
  setFilteredMultiOpeningLineItems: (filteredMultiOpeningLineItems) =>
    set({ filteredMultiOpeningLineItems }),

  // Selection state
  selectedItems: [],
  setSelectedItems: (selectedItems) => set({ selectedItems }),
  globalSelect: false,
  setGlobalSelect: (globalSelect) => set({ globalSelect }),

  // Line item filters
  selectedPapers: [],
  setSelectedPapers: (selectedPapers) => set({ selectedPapers }),
  selectedServices: [],
  setSelectedServices: (selectedServices) => set({ selectedServices }),
  selectedStatus: [],
  setSelectedStatus: (selectedStatus) => set({ selectedStatus }),

  // Details state
  notes: {
    client: '',
    admin: '',
    boutique: '',
  },
  setNotes: (notes) => set({ notes }),
  activeTab: 'client',
  setActiveTab: (activeTab) => set({ activeTab }),
  isNotesModalOpen: false,
  setIsNotesModalOpen: (isNotesModalOpen) => set({ isNotesModalOpen }),

  // Address state
  address: {
    street: '',
    street2: '',
    city: '',
    stateProvince: '',
    zip: '',
    firstName: '',
    lastName: '',
    recipient: '',
  },
  setAddress: (address) => set({ address }),
  isAddressModalOpen: false,
  setIsAddressModalOpen: (isAddressModalOpen) => set({ isAddressModalOpen }),

  // Refund state
  refund: {
    amount: '',
    reason: '',
  },
  setRefund: (refund) => set({ refund }),
  isRefundModalOpen: false,
  setIsRefundModalOpen: (isRefundModalOpen) => set({ isRefundModalOpen }),

  // Loading states for all orders and single order
  ordersLoading: false,
  setOrdersLoading: (ordersLoading) => set({ ordersLoading }),
  orderLoading: false,
  setOrderLoading: (orderLoading) => set({ orderLoading }),
  orderMutationLoading: false,

  // Updating states
  statusUpdating: false,
  setStatusUpdating: (statusUpdating) => set({ statusUpdating }),
  error: null,
  setError: (error) => set({ error }),

  // Navigation/Order Status states
  orderStatus: null,
  setOrderStatus: (orderStatus) => set({ orderStatus }),
  isTrackingModalOpen: false,
  setIsTrackingModalOpen: (isTrackingModalOpen) => set({ isTrackingModalOpen }),
  trackingUrl: '',
  setTrackingUrl: (trackingUrl) => set({ trackingUrl }),
  trackingLoading: false,
  setTrackingLoading: (trackingLoading) => set({ trackingLoading }),
  downloadLink: '',
  setDownloadLink: (downloadLink) => set({ downloadLink }),
  createZip: false,
  setCreateZip: (createZip) => set({ createZip }),
  progressPercent: 0,
  setProgressPercent: (progressPercent) => set({ progressPercent }),

  // API Functions
  fetchOrders: async () => {
    try {
      set({ ordersLoading: true, error: null });
      const { data, meta } = await httpClient('lab_orders');
      set({ orders: data, metadata: meta, ordersLoading: false });
    } catch (err) {
      set({ error: apiErrorMessage(err), ordersLoading: false });
    }
  },

  getOrder: async (id) => {
    set({ orderLoading: true, error: null });
    try {
      const data = await httpClient(`lab_orders/${id}`);
      set({ order: data, orderLoading: false });
      
      const { lineItems, multiOpeningLineItems, albumOrderDetails } = partitionIncluded(data?.included);
      const currentOrder = data?.data;
      const attributes = currentOrder?.attributes;

      // Split recipient into firstName and lastName
      const recipient = attributes?.shippingAddress?.recipient ?? '';
      const { firstName, lastName } = splitRecipientName(recipient);

      set({
        lineItems,
        originalLineItems: lineItems,
        filteredLineItems: lineItems,
        albumOrderDetails,
        filteredAlbumOrderDetails: albumOrderDetails,
        multiOpeningLineItems,
        filteredMultiOpeningLineItems: multiOpeningLineItems,
        isAdmin: data?.meta?.currentUserIsAdmin,
        labOrderId: currentOrder?.attributes?.guid,
        orderStatus: attributes?.status,
        trackingUrl: attributes?.trackingUrl,
        downloadLink: attributes?.downloadAllLink,
        notes: {
          client: clientNotes(attributes?.notes),
          admin: attributes?.adminNotes ?? '',
          boutique: attributes?.boutiqueShippingNote ?? '',
        },
        address: {
          street: attributes?.shippingAddress?.street ?? '',
          street2: attributes?.shippingAddress?.street2 ?? '',
          city: attributes?.shippingAddress?.city ?? '',
          stateProvince: attributes?.shippingAddress?.stateProvince ?? '',
          zip: attributes?.shippingAddress?.zip ?? '',
          firstName: firstName,
          lastName: lastName,
          recipient: recipient,
        },
        refund: {
          amount: attributes?.refundAmount ?? '',
          reason: attributes?.refundNote ?? '',
        },
      });
    } catch (err) {
      set({ error: apiErrorMessage(err), orderLoading: false });
    }
  },

  updateOrder: async (id, type, value) => {
    set({ error: null });
    try {
      // Ensure value is always a string (never undefined)
      const payloadValue = value ?? "";
      
      const response = await httpClient.patch(`lab_orders/${id}`, { 
        type, 
        value: payloadValue 
      });
      const updatedOrder = response?.data;
      const updatedAttributes = updatedOrder?.attributes;
      
      const updatedNotes = {
        client: clientNotes(updatedAttributes?.notes),
        admin: updatedAttributes?.internalNotes ?? '',
        boutique: updatedAttributes?.boutiqueShippingNote ?? '',
      };
      
      set({ 
        order: response, 
        orderStatus: updatedAttributes?.status || get().orderStatus,
        trackingUrl: updatedAttributes?.trackingUrl || get().trackingUrl,
        downloadLink: updatedAttributes?.downloadAllLink || get().downloadLink,
        notes: updatedNotes,
      });

      // Update orders list if it exists
      const { orders } = get();
      if (orders) {
        set({
          orders: orders.map((o) =>
            o.attributes.guid === id ? response.data : o
          ),
        });
      }
    } catch (err) {
      set({ error: apiErrorMessage(err) });
    }
  },

  refundOrder: async (id, refundAmount, refundNote) => {
    set({ error: null });
    try {
      const response = await httpClient.post(
        `lab_orders/${id}/refund`,
        {
          refund_amount: refundAmount,
          refund_note: refundNote,
        }
      );
      set({ order: response });
    } catch (err) {
      set({ error: apiErrorMessage("") });
    }
  },

  updateLineItemStatus: async (e, selectedLineItems, labOrderId) => {
    set({ error: null });
    try {
      const response = await httpClient.patch(
        `/lab_orders/${labOrderId}/line_items/update_multiple_line_item_status`,
        {
          ids: [...selectedLineItems],
          value: e.target.value
        }
      );

      // Update line items
      const { lineItems, multiOpeningLineItems, albumOrderDetails } = partitionIncluded(response?.included);
      const currentState = get();
      set({
        order: mergeOrderResponse(currentState.order, response),
        orders: currentState.orders
          ? currentState.orders.map((order) => (
            order.attributes.guid === labOrderId
              ? mergeOrderData(order, response.data)
              : order
          ))
          : currentState.orders,
        lineItems,
        originalLineItems: lineItems,
        filteredLineItems: lineItems,
        albumOrderDetails: albumOrderDetails.length
          ? albumOrderDetails
          : currentState.albumOrderDetails,
        filteredAlbumOrderDetails: albumOrderDetails.length
          ? albumOrderDetails
          : currentState.filteredAlbumOrderDetails,
        multiOpeningLineItems,
        filteredMultiOpeningLineItems: multiOpeningLineItems,
      });
    } catch (err) {
      set({ error: apiErrorMessage(err) });
    }
  },

  updateLineItem: async (type, value, labOrderId, lineItemGuid) => {
    set({ error: null });
    try {
      const response = await httpClient.patch(
        `/lab_orders/${labOrderId}/line_items/${lineItemGuid}`,
        { type, value }
      );

      // Update line items
      const { lineItems, multiOpeningLineItems, albumOrderDetails } = partitionIncluded(response?.included);
      const currentState = get();
      set({
        order: mergeOrderResponse(currentState.order, response),
        orders: currentState.orders
          ? currentState.orders.map((order) => (
            order.attributes.guid === labOrderId
              ? mergeOrderData(order, response.data)
              : order
          ))
          : currentState.orders,
        lineItems,
        originalLineItems: lineItems,
        filteredLineItems: lineItems,
        albumOrderDetails: albumOrderDetails.length
          ? albumOrderDetails
          : currentState.albumOrderDetails,
        filteredAlbumOrderDetails: albumOrderDetails.length
          ? albumOrderDetails
          : currentState.filteredAlbumOrderDetails,
        multiOpeningLineItems,
        filteredMultiOpeningLineItems: multiOpeningLineItems,
      });
    } catch (error) {
      set({ error: apiErrorMessage(error) });
    }
  },

  deleteLabOrderAlbum: async (albumGuid) => {
    const labOrderGuid = get().labOrderId;
    if (!labOrderGuid) return;
    set({ error: null, orderMutationLoading: true });
    try {
      const response = await httpClient.delete(`lab_orders/${labOrderGuid}/albums/${albumGuid}`);
      const { lineItems, multiOpeningLineItems, albumOrderDetails } = partitionIncluded(response?.included);
      set({
        lineItems,
        originalLineItems: lineItems,
        filteredLineItems: lineItems,
        albumOrderDetails,
        filteredAlbumOrderDetails: albumOrderDetails,
        multiOpeningLineItems,
        filteredMultiOpeningLineItems: multiOpeningLineItems,
        orderMutationLoading: false,
      });
    } catch (err) {
      set({ error: apiErrorMessage(err), orderMutationLoading: false });
    }
  },

  searchOrders: async (query) => {
    set({ ordersLoading: true, error: null });
    try {
      const { data, meta } = await httpClient(`lab_orders/search?${query}`);
      set({ orders: data, metadata: meta, ordersLoading: false });
    } catch (err) {
      set({ error: apiErrorMessage(err), ordersLoading: false });
    }
  },

  createZipFile: async (id) => {
    set({ createZip: true, error: null });
    try {
      const response = await httpClient.post(`lab_orders/${id}/create_zip_file`);
      const newLink = response.data?.attributes?.downloadAllLink;
      if (newLink) {
        set({ downloadLink: newLink, createZip: false });
      } else {
        set({ createZip: false });
      }
    } catch (err) {
      set({ error: apiErrorMessage(err), createZip: false });
    }
  },

  updateTrackingUrl: async (id, trackingUrl) => {
    set({ trackingLoading: true, error: null });
    try {
      await get().updateOrder(id, 'tracking_url', trackingUrl);
      set({ trackingLoading: false, isTrackingModalOpen: false });
    } catch (err) {
      set({ trackingLoading: false });
    }
  },

  updateOrderStatus: async (id, status) => {
    const currentStatus = get().orderStatus;
    if (status !== currentStatus) {
      set({ statusUpdating: true });
      await get().updateOrder(id, 'status', status);
      set({ statusUpdating: false });
    }
  },

  calculateOrderProgress: (statusArray, currentOrder) => {
    if (!Array.isArray(statusArray) || statusArray.length === 0) {
      set({ progressPercent: 0 });
      return;
    }
    const hasReadyToShipItems = statusArray.includes('ready_to_ship');
    if (currentOrder?.attributes?.status === 'shipped' && !hasReadyToShipItems) {
      set({ progressPercent: 100 });
      return;
    }

    const totalCount = statusArray.length;
    const completedCount = statusArray.reduce((count, status) => (
      status === 'ready_to_ship' ? count + 1 : count
    ), 0);
    const completionPercent = Math.round((completedCount / totalCount) * 100);
    const safePercent = Number.isNaN(completionPercent) ? 0 : completionPercent;
    set({ progressPercent: safePercent });
  },

  // Download line item zip file
  downloadLineItemZip: async (lineItemGuid, isMultiOpening = false) => {
    try {
      const endpoint = isMultiOpening
        ? `lab_orders/${get().labOrderId}/multi_openings_line_items/${lineItemGuid}/download_zip`
        : `api_lab_order_line_items/${lineItemGuid}/download_zip`;
      const response = await httpClient.post(
        endpoint,
      );
      const downloadUrl = response?.downloadUrl;
      if (downloadUrl) {
        window.location.href = downloadUrl;
      }
      return response;
    } catch (err) {
      set({ error: apiErrorMessage(err) });
      throw err;
    }
  },

  downloadAlbumZip: async (albumGuid) => {
    try {
      const response = await httpClient.post(
        `lab_orders/${get().labOrderId}/albums/${albumGuid}/download_zip`,
      );
      if (response?.downloadUrl) window.location.href = response.downloadUrl;
      return response;
    } catch (err) {
      set({ error: apiErrorMessage(err) });
      throw err;
    }
  },

  // Update shipping address
  updateShippingAddress: async (addressGuid, addressData) => {
    set({ error: null });
    try {
      // Combine firstName and lastName into recipient
      const recipient = combineRecipientName(addressData.firstName, addressData.lastName);
      const response = await httpClient.patch(`/addresses/${addressGuid}`, {
        street: addressData.street,
        street_2: addressData.street2,
        city: addressData.city,
        stateProvince: addressData.stateProvince,
        zip: addressData.zip,
        recipient: recipient
      });

      const { data } = response;
      const updatedAddress = data?.attributes;
      
      // Split recipient for display
      const updatedRecipient = updatedAddress?.recipient ?? '';
      const { firstName, lastName } = splitRecipientName(updatedRecipient);
      
      // Update address in store
      set({
        address: {
          street: updatedAddress?.street ?? "",
          street2: updatedAddress?.street_2 ?? "",
          city: updatedAddress?.city ?? "",
          stateProvince: updatedAddress?.stateProvince ?? "",
          zip: updatedAddress?.zip ?? "",
          firstName: firstName,
          lastName: lastName,
          recipient: updatedRecipient
        },
      });

      // Update order if it exists
      const { order } = get();
      if (order?.data?.attributes?.shippingAddress) {
        set({
          order: {
            ...order,
            data: {
              ...order.data,
              attributes: {
                ...order.data.attributes,
                shippingAddress: {
                  ...order.data.attributes.shippingAddress,
                  ...updatedAddress
                }
              }
            }
          }
        });
      }

      return response;
    } catch (err) {
      set({ error: apiErrorMessage(err) });
      throw err;
    }
  },

  resetNotesFromOrder: () => {
    const { order } = get();
    const attributes = order?.data?.attributes;
    if (attributes) {
      set({
        notes: {
          client: clientNotes(attributes?.notes),
          admin: attributes?.internalNotes ?? '',
          boutique: attributes?.boutiqueShippingNote ?? '',
        },
      });
    }
  },

  resetAddressFromOrder: () => {
    const { order } = get();
    const attributes = order?.data?.attributes;
    if (attributes?.shippingAddress) {
      // Split recipient into firstName and lastName
      const recipient = attributes?.shippingAddress?.recipient ?? '';
      const { firstName, lastName } = splitRecipientName(recipient);
      
      set({
        address: {
          street: attributes?.shippingAddress?.street ?? '',
          street2: attributes?.shippingAddress?.street2 ?? '',
          city: attributes?.shippingAddress?.city ?? '',
          stateProvince: attributes?.shippingAddress?.stateProvince ?? '',
          zip: attributes?.shippingAddress?.zip ?? '',
          firstName: firstName,
          lastName: lastName,
          recipient: recipient,
        },
      });
    }
  },

  resetRefundFromOrder: () => {
    const { order } = get();
    const attributes = order?.data?.attributes;
    if (attributes) {
      set({
        refund: {
          amount: attributes?.refundAmount ?? '',
          reason: attributes?.refundNote ?? '',
        },
      });
    }
  },

}));

export default useLabOrderDetailsStore;
